/**
 * StatisticalSummaryModal.js
 *
 * Accepts one (current week) or two (current + previous week) CSV files,
 * submits them to POST /statistical-summary/analyze, and opens the
 * returned HTML reports in new browser tabs using blob URLs so the exact
 * same output as the Data Tool (A1 / A2 / A3) is displayed.
 *
 * Styling re-uses existing protest-modal / deep-research-modal classes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icons } from './Icons';

const ACCEPTED = '.csv,text/csv,application/vnd.ms-excel';

function FilePicker({ label, sublabel, file, onFileChange, disabled, inputRef }) {
  return (
    <div className="protest-location-section">
      <h3>{label}</h3>
      {sublabel && <p className="deep-research-modal-desc" style={{ marginTop: 0 }}>{sublabel}</p>}
      <div className="protest-location-field">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            disabled={disabled}
          />
          <button
            type="button"
            className="protest-btn-cancel"
            style={{ minWidth: 100 }}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            {file ? 'Change file' : 'Choose CSV…'}
          </button>
          {file && (
            <span style={{ fontSize: '0.9em', color: '#444', wordBreak: 'break-all' }}>
              {file.name}
              <button
                type="button"
                aria-label="Remove file"
                style={{
                  marginLeft: 6,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#888',
                  fontSize: '1em',
                  verticalAlign: 'middle',
                }}
                onClick={() => {
                  onFileChange(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatisticalSummaryModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) {
  const [currentFile, setCurrentFile]   = useState(null);
  const [previousFile, setPreviousFile] = useState(null);
  const [error, setError]               = useState('');

  const currentInputRef  = useRef(null);
  const previousInputRef = useRef(null);

  // Reset state every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setCurrentFile(null);
    setPreviousFile(null);
    setError('');
    if (currentInputRef.current)  currentInputRef.current.value  = '';
    if (previousInputRef.current) previousInputRef.current.value = '';
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!currentFile) {
      setError('Please select a current-week CSV file.');
      return;
    }
    setError('');
    onSubmit({ currentFile, previousFile });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div className="protest-modal-overlay" onClick={handleOverlayClick}>
      <div className="protest-modal deep-research-modal">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="protest-modal-header">
          <div className="protest-modal-title">
            <Icons.FileText />
            <h2>Statistical Summary</h2>
          </div>
          <button
            type="button"
            className="protest-modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <Icons.Close />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="protest-modal-content">
          <p className="deep-research-modal-desc">
            Upload a weekly cyberattack CSV (Manual TG Upload Form format) to
            generate an A1 weekly dashboard and A3 threat actor spotlight.
            Optionally upload the previous week's CSV as well for an A2
            two-week comparative analysis. Reports open in new browser tabs.
          </p>

          <FilePicker
            label="Current week CSV"
            sublabel="Required — the week you want to analyse."
            file={currentFile}
            onFileChange={setCurrentFile}
            disabled={isLoading}
            inputRef={currentInputRef}
          />

          <FilePicker
            label="Previous week CSV (optional)"
            sublabel="When provided, a two-week comparative (A2) report is also generated."
            file={previousFile}
            onFileChange={setPreviousFile}
            disabled={isLoading}
            inputRef={previousInputRef}
          />

          {error && <div className="protest-error">{error}</div>}

          {isLoading && (
            <div className="deep-research-modal-progress">
              Generating reports — please wait…
            </div>
          )}

          <div className="protest-modal-actions">
            <button
              type="button"
              className="protest-btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="protest-btn-submit"
              disabled={isLoading || !currentFile}
            >
              {isLoading ? (
                <>
                  <span className="protest-loading-spinner" />
                  Generating…
                </>
              ) : (
                <>
                  <Icons.Search />
                  Generate reports
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StatisticalSummaryModal;
